import React, { useState, useEffect } from 'react';
import { AlertCircle, Calendar, User, Award, AlertTriangle, XCircle } from 'lucide-react';
import useCertificationManagementStore from '@/stores/AdminStore/CertificationManagementStore';
import './ExpiryRenewals.css';

const ExpiryRenewals: React.FC = () => {
  const {
    expiryRenewals,
    expiryRenewalsPagination,
    loading,
    error,
    fetchExpiryRenewals,
    clearError,
  } = useCertificationManagementStore();

  const [filter, setFilter] = useState<'expiring_soon' | 'expired' | 'all'>('expiring_soon');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  // Fetch expiry renewals when filter or page changes
  useEffect(() => {
    fetchExpiryRenewals(filter, currentPage, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, currentPage]);

  const handleFilterChange = (newFilter: 'expiring_soon' | 'expired' | 'all') => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const getDaysUntilExpiryColor = (days: number) => {
    if (days < 0) return 'days-expired';
    if (days <= 7) return 'days-critical';
    if (days <= 30) return 'days-warning';
    return 'days-normal';
  };

  const getDaysUntilExpiryText = (days: number) => {
    if (days < 0) {
      const expiredDays = Math.abs(days);
      return `Expired ${expiredDays} ${expiredDays === 1 ? 'day' : 'days'} ago`;
    }
    if (days === 0) return 'Expires today';
    if (days === 1) return 'Expires tomorrow';
    return `${days} days remaining`;
  };

  const getStatusIcon = (status: 'expiring_soon' | 'expired') => {
    if (status === 'expired') {
      return <XCircle size={16} className="status-icon-expired" />;
    }
    return <AlertTriangle size={16} className="status-icon-warning" />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="admin-certifi-expiry-renewals">
      {/* Error Alert */}
      {error && (
        <div className="admin-certifi-alert admin-certifi-alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={clearError}>×</button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="admin-certifi-expiry-tabs">
        <button
          className={`admin-certifi-expiry-tab ${filter === 'expiring_soon' ? 'active' : ''}`}
          onClick={() => handleFilterChange('expiring_soon')}
        >
          <AlertTriangle size={16} />
          Expiring Soon
          {filter === 'expiring_soon' && expiryRenewalsPagination && (
            <span className="tab-badge">{expiryRenewalsPagination.total}</span>
          )}
        </button>
        <button
          className={`admin-certifi-expiry-tab ${filter === 'expired' ? 'active' : ''}`}
          onClick={() => handleFilterChange('expired')}
        >
          <XCircle size={16} />
          Expired
          {filter === 'expired' && expiryRenewalsPagination && (
            <span className="tab-badge">{expiryRenewalsPagination.total}</span>
          )}
        </button>
        <button
          className={`admin-certifi-expiry-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
          {filter === 'all' && expiryRenewalsPagination && (
            <span className="tab-badge">{expiryRenewalsPagination.total}</span>
          )}
        </button>
      </div>

      {/* Loading State */}
      {loading && expiryRenewals.length === 0 ? (
        <div className="admin-certifi-loading">
          <div className="spinner"></div>
          <p>Loading expiry renewals...</p>
        </div>
      ) : expiryRenewals.length === 0 ? (
        <div className="admin-certifi-empty-state">
          {filter === 'expiring_soon' ? (
            <>
              <Calendar size={48} />
              <h3>No Certifications Expiring Soon</h3>
              <p>There are no certifications expiring in the next 30 days.</p>
            </>
          ) : filter === 'expired' ? (
            <>
              <XCircle size={48} />
              <h3>No Expired Certifications</h3>
              <p>There are no expired certifications at this time.</p>
            </>
          ) : (
            <>
              <Calendar size={48} />
              <h3>No Renewal Data Available</h3>
              <p>No certification renewals data is available.</p>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Expiry Table */}
          <div className="admin-certifi-expiry-table-container">
            <table className="admin-certifi-expiry-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Certification</th>
                  <th>Issued Date</th>
                  <th>Expiry Date</th>
                  <th>Days Until Expiry</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {expiryRenewals.map((entry) => (
                  <tr key={entry.enrollment_id}>
                    <td>
                      <div className="admin-certifi-student-info">
                        <div className="admin-certifi-student-avatar">
                          <User size={20} />
                        </div>
                        <div>
                          <div className="admin-certifi-student-name">{entry.student_name}</div>
                          <div className="admin-certifi-student-email">{entry.student_email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="admin-certifi-cert-name">
                        <Award size={16} />
                        {entry.certification_name}
                      </div>
                    </td>
                    <td>{formatDate(entry.certificate_issued_date)}</td>
                    <td>{formatDate(entry.expiry_date)}</td>
                    <td>
                      <span className={`admin-certifi-days-badge ${getDaysUntilExpiryColor(entry.days_until_expiry)}`}>
                        <Calendar size={14} />
                        {getDaysUntilExpiryText(entry.days_until_expiry)}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-certifi-status-badge admin-certifi-status-${entry.status}`}>
                        {getStatusIcon(entry.status)}
                        {entry.status === 'expiring_soon' ? 'Expiring Soon' : 'Expired'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {expiryRenewalsPagination && expiryRenewalsPagination.totalPages > 1 && (
            <div className="admin-certifi-pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
                className="admin-certifi-pagination-btn"
              >
                Previous
              </button>
              <span className="admin-certifi-pagination-info">
                Page {currentPage} of {expiryRenewalsPagination.totalPages} ({expiryRenewalsPagination.total} total)
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(expiryRenewalsPagination.totalPages, prev + 1))}
                disabled={currentPage === expiryRenewalsPagination.totalPages || loading}
                className="admin-certifi-pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExpiryRenewals;
